import type { Writable } from "@glideapps/ts-necessities";
import { areEqual, definedMap, hasOwnProperty, withDefault } from "collection-utils";
import deepEqual from "deep-equal";
import { isLeft } from "fp-ts/lib/Either";
import debounce from "lodash/debounce";
import toPairs from "lodash/toPairs";
import type { ChangeObservable } from "../../../change-obserable";
import { Watchable } from "../../../change-obserable";
import { ComputationModelImpl } from "../../../computation-model/computed-columns";
import type { Row } from "../../../computation-model/data";
import { ComputationModelDataRowStore } from "../../../computation-model/data-row-store";
import { TableKeeperStore } from "../../../computation-model/table-keeper-store";
import type { Handler } from "../../../computation-model/types";
import type {
    Database,
    DataSnapshot,
    DiffResults,
    DocumentData,
    DocumentDataWithID,
    RowDocumentDatas,
    RowIndex,
    UserSpecificRow,
} from "../../../Database";
import {
    combineRowDocumentDatas,
    deleteRowDocumentData,
    listenToPublishedAppData,
    makeAppUserForAppFromDocument,
    setRowDocumentData,
    userSpecificRowCodec,
} from "../../../Database";
import type { TableName } from "../../../database-strings";
import {
    documentIDForTableName,
    getNativeTableIDFromTableName,
    makeAppUsersForAppPath,
    rowVersionFieldName,
} from "../../../database-strings";
import type { DiffResult } from "../../../Database/core";
import { Eminence, eminenceFlags } from "../../../Database/eminence";
import { QuotaKind } from "../../../Database/quotas";
import { logError, logInfo } from "../../../debug-print";
import { getFeatureSetting } from "../../../feature-settings";
import type { BaseRowIndex } from "../../../firebase-function-types";
import { checkBaseRowIndex, isBaseRowIndex } from "../../../firebase-function-types";
import type { SchemaInspector } from "../../../generator/components";
import { makeSimpleSchemaInspector } from "../../../generator/components";
import type { Formula, TableColumn, TableGlideType, TypeSchema } from "../../../generator/description";
import {
    findTable,
    getEmailOwnersColumnNames,
    getTableColumn,
    getTableName,
    isComputedColumn,
    isolateTypeSchema,
    isPrimitiveType,
    isUserAgnosticDataColumn,
    makeTypeSchema,
    tableHasRowOwners,
} from "../../../generator/description";
import { compareSchemas, setEmailOwnersColumns } from "../../../generator/description-utils";
import { rowIndexColumnName } from "../../../google-sheets";
import { makeRowID } from "../../../make-row-id";
import {
    areSetsEqual,
    assert,
    assertNever,
    checkString,
    defined,
    dontAwait,
    isArray,
    mapFilterUndefined,
    normalizeEmailAddress,
    objectWithUndefinedProperties,
    panic,
} from "../../../Support";
import { TemporaryUndefinedContainer } from "../../../temporary-undefined-container";
import { frontendTrace } from "../../../tracing";
import { isLoadedDefined, loadedDefinedMap, SettableForwardingValueProducer } from "../../component-helpers";
import type { PrimitiveValue } from "../../computation-types";
import { BindEnvironmentEvaluable, isObjectValue, LOADING } from "../../computation-types";
import type { ComputedColumnProvider, ValuesAndDatas } from "../../data";
import {
    ArrayRefValueProducer,
    Data as DataData,
    extractColumnValuesFromValueProducer,
    RowData,
    TableData,
} from "../../data";
import { sortByRowIndex } from "../../table-index";
import type {
    AppEnvironment,
    AppFacilities,
    AppOther,
    AppUserAuthenticator,
    ComputationModel,
    DataStore,
    EnvironmentBoundEvaluable,
    MinimalAppEnvironment,
    ObjectValue,
    OfflineQueue,
    ValueProducer,
} from "../../types";
import { Data } from "../../types";
import type { AppUserData, DataOrForwarder, DataRowStore, TableDataEntries, TableDataHelper } from "../data-row-store";
import { isNewRowOwnedByUser, isRowOwnedByUser } from "../is-row-owned";
import type { DataStoreEnvironmentAccessors, DurableStorageController } from "./durable-storage-controller";
import { DummyDurableStorageController } from "./durable-storage-controller/dummy";
import { NonblockingDurableStorageController } from "./durable-storage-controller/nonblocking";
import type { FieldNameCache } from "./field-names";
import { convertFieldNamesToColumnNamesWithMapping, makeColumnMappingForJSONFromDocumentData } from "./field-names";
import type { FirestoreTableListener } from "./listener";
import { FirestoreGoogleSheetsTableListener, FirestoreNativeTableListener } from "./listener";
import { RefValueProducer } from "./old-style-refs";
import {
    compareUserSpecificRowsForApplication,
    convertJSONObjectToRowData,
    liftUserSpecificData,
    updateRowData,
} from "./row-data";
import type {
    AddRowQueueItem,
    DataForTables,
    DocumentsFirestoreHandler,
    TableFetcher,
    TableMutationHandler,
} from "./types";

function getForwardee(data: Data | SettableForwardingValueProducer<AppOther, AppEnvironment>): Data {
    if (data instanceof SettableForwardingValueProducer) {
        data = defined(data.forwardee) as Data;
        assert(data instanceof DataData);
    }
    return data;
}

function getRowIndexForDocumentData(data: DocumentData): BaseRowIndex | undefined {
    const index = data[rowIndexColumnName];
    if (!isBaseRowIndex(index)) return undefined;
    return index;
}

function getRowVersionForDocumentData(data: DocumentData): number | undefined {
    const index = data[rowVersionFieldName];
    if (typeof index !== "number") return undefined;
    return index;
}

function getForRowDocumentDatas<T>(
    { publicData, privateData }: RowDocumentDatas,
    get: (data: DocumentData) => T
): T | undefined {
    if (publicData !== undefined) {
        const v = get(publicData);
        if (v !== undefined) return v;
    }
    if (privateData !== undefined) {
        return get(privateData);
    }
    return undefined;
}

function getRowIndex(datas: RowDocumentDatas): BaseRowIndex | undefined {
    return getForRowDocumentDatas(datas, getRowIndexForDocumentData);
}

function isOldRowOwnedByUser(
    row: Data,
    appUserData: AppUserData | undefined,
    table: TableGlideType | undefined
): boolean {
    let current: ObjectValue | undefined;

    return isRowOwnedByUser(appUserData, table, columnName => {
        if (current === undefined) {
            const rowValue = row.current;
            assert(isObjectValue(rowValue));
            current = rowValue;
        }
        const v = current[columnName]?.current;

        if (typeof v === "string") {
            return v;
        } else if (Array.isArray(v)) {
            return mapFilterUndefined(v, vp => {
                const c = vp.current;
                if (typeof c !== "string") return undefined;
                return c;
            });
        } else {
            return undefined;
        }
    });
}

// When a table has row owners, the builder has to keep two sets of data
// for each table: the set for the app, which is filtered by the row
// owner, and the set for the builder, which is unfiltered.  It's not as
// trivial as just filtering the rows, because relations also have to
// apply to just the filtered data, so in that case each row actually
// has two `Data`s with two different sets of computed columns.  When
// Glide is running in the player there is only one set, which is
// filtered via a Firestore query.
//
// The `DataRowStore` keeps all data that is separate between the
// builder vs the app view.  It's not a very clean abstraction.  In
// particular, see the comment in `updateAppWithTableData` in the
// `TableDataHandler`.
class ValueProducerDataRowStore implements DataRowStore, ComputedColumnProvider<AppOther, AppEnvironment> {
    private _appData: TableData<AppOther, AppEnvironment> | undefined;
    private _appDataRows = new Map<string, Data>();
    // [data, row ID]
    private _addedRows: [Data, string | undefined][] = [];

    constructor(
        private readonly _db: Database,
        private readonly _helper: TableDataHelper,
        // `_inBuilder` is `true` iff we're running in the builder
        private readonly _isBuilder: boolean,
        // `_forDataEditor` is `true` iff we're storing data for
        // the data editor.
        private readonly _forDataEditor: boolean,
        // `_filterByEmail` means we have to manually filter the rows
        // by email address, which is the case when we're storing the
        // app data, and we're running in the builder.
        private readonly _filterByEmail: boolean
    ) {}

    private isRowOwnedByUser(row: Data, appUserData: AppUserData | undefined): boolean {
        return isOldRowOwnedByUser(row, appUserData, this._helper.getTable());
    }

    private isRowIncluded(row: Data, appUserData: AppUserData | undefined): boolean {
        if (!this._filterByEmail) return true;
        return this.isRowOwnedByUser(row, appUserData);
    }

    private getAppDataRows(sortedEntries: TableDataEntries): Data[] {
        const appUserData = this._helper.getAppUserData();

        // The key might not be in `_appDataRows` because we didn't add it due to
        // the row quota.
        return mapFilterUndefined(sortedEntries, ([key]) => this.getDataRow(key, appUserData)).concat(
            this._addedRows.map(v => v[0])
        );
    }

    public getAllComputedColumnNames(): readonly string[] | undefined {
        const table = this._helper.getTable();
        if (table === undefined) return undefined;

        return table.columns.filter(isComputedColumn).map(c => c.name);
    }

    private makeComputedColumnForRow(rowData: Data, column: TableColumn): ValueProducer | undefined {
        const { formula } = column;
        if (formula === undefined) return undefined;

        const evaluable = this._helper.makeComputedColumn(column.name, formula, this._isBuilder, this._forDataEditor);
        if (evaluable === undefined) return undefined;

        return evaluable.eval(rowData, undefined);
    }

    public makeComputedColumn(row: RowData<AppOther, AppEnvironment>, columnName: string): ValueProducer | undefined {
        const table = this._helper.getTable();
        if (table === undefined) return undefined;

        const column = getTableColumn(table, columnName);
        if (column === undefined) return undefined;

        return this.makeComputedColumnForRow(row, column);
    }

    private getAppData(): TableData<AppOther, AppEnvironment> | undefined {
        const data = this._helper.getData(this._forDataEditor);
        if (data === undefined) return undefined;

        if (this._appData === undefined) {
            if (data.current !== LOADING) {
                const forwardee = getForwardee(data);
                assert(forwardee instanceof TableData);
                this._appData = forwardee;
            } else if (data instanceof SettableForwardingValueProducer) {
                const newData = new TableData<AppOther, AppEnvironment>([], this);
                data.setForwardee(newData);
                this._appData = newData;
            } else {
                return panic("We have a Data that's LOADING, which shouldn't happen");
            }
        }
        return this._appData;
    }

    public setAppDataForSortedEntries(sortedEntries: TableDataEntries): number | undefined {
        const appData = this.getAppData();
        if (appData === undefined) {
            logError("Update for non-existant table");
            return;
        }

        const results = this.getAppDataRows(sortedEntries);

        // FIXME: We reset the array every time, triggering a dirtying.  Can we not do this?
        appData.set(results);

        return results.length;
    }

    private getExistingOrAddedRow(id: string, isAdded: boolean, rowID: string | undefined): Data | undefined {
        let existingAppRow = this._appDataRows.get(id);
        if (existingAppRow === undefined && isAdded) {
            // See https://github.com/quicktype/glide/issues/2529
            if (rowID !== undefined) {
                const index = this._addedRows.findIndex(r => r[1] === rowID);
                if (index >= 0) {
                    existingAppRow = this._addedRows[index][0];
                    this._addedRows.splice(index, 1);
                }
            } else {
                existingAppRow = this._addedRows.shift()?.[0];
            }
            if (existingAppRow !== undefined) {
                this._appDataRows.set(id, existingAppRow);
            }
        }
        return existingAppRow;
    }

    public updateRow(
        id: string,
        isAdded: boolean,
        mayAddRow: boolean,
        updates: DocumentData,
        userDataUpdates: DocumentData | undefined,
        fullData: DocumentData,
        fullUserData: DocumentData | undefined
    ): { reprocessUserSpecificData: boolean; didAddRow: boolean } | undefined {
        const table = this._helper.getTable();
        if (table === undefined) return undefined;

        const { rowIDColumn } = table;

        let reprocessUserSpecificData = false;
        let didAddRow = false;

        const existingAppRow = this.getExistingOrAddedRow(
            id,
            isAdded,
            definedMap(rowIDColumn, c => fullData[c])
        );
        if (existingAppRow !== undefined) {
            if (rowIDColumn !== undefined && hasOwnProperty(updates, rowIDColumn)) {
                // The row ID for a row has changed, so we will reprocess all
                // user-specific data, since we keep it by row index.
                reprocessUserSpecificData = true;
            }
            updateRowData(this._db, existingAppRow, { ...updates, ...userDataUpdates }, table, false);
        } else if (mayAddRow) {
            if (!isAdded) {
                logError("Modified row doesn't exist yet", id);
            }

            didAddRow = true;

            const appRowData = this.makeDataForRowObject(fullData, fullUserData, table);
            if (appRowData !== undefined) {
                this.setDataRow(id, appRowData);
            }
        }

        return { reprocessUserSpecificData, didAddRow };
    }

    private findRow(
        rowIndex: RowIndex
    ):
        | { appData: TableData<AppOther, AppEnvironment>; row: ValueProducer; rowObject: ObjectValue; index: number }
        | undefined {
        const appData = this.getAppData();
        const rows = appData?.current;
        if (rows === undefined || !isArray(rows)) {
            logError("No data when trying to add row");
            return;
        }

        let keyColumnName: string;
        let keyColumnValue: unknown;
        if (isBaseRowIndex(rowIndex)) {
            keyColumnName = rowIndexColumnName;
            keyColumnValue = rowIndex;
        } else {
            keyColumnName = rowIndex.keyColumnName;
            keyColumnValue = rowIndex.keyColumnValue;
        }
        assert(keyColumnValue !== undefined);

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const rowObject = row.current;
            if (!isObjectValue(rowObject)) continue;

            const column = rowObject[keyColumnName];
            if (column?.current === keyColumnValue) {
                return { appData: defined(appData), row, rowObject, index: i };
            }
        }
        return undefined;
    }

    public addRowWithValues(
        values: ValuesAndDatas<AppOther, AppEnvironment>,
        rowID: string | undefined
    ): ValueProducer | undefined {
        const table = this._helper.getTable();
        if (table === undefined) return undefined;

        const appData = this.getAppData();
        if (appData === undefined) {
            return undefined;
        }

        const valuesCopy: ObjectValue = { ...values.datas };
        const newRowData = new RowData(valuesCopy, appData);

        // FIXME: Use this way to add array columns whenever we load rows from Firestore,
        // then we won't need the array ref objects anymore at all.
        for (const c of table.columns) {
            if (
                isComputedColumn(c) ||
                c.type.kind !== "array" ||
                c.type.itemColumnNames === undefined ||
                !isPrimitiveType(c.type.items)
            ) {
                continue;
            }

            valuesCopy[c.name] = new ArrayRefValueProducer(c.type.itemColumnNames, newRowData);
        }

        // this.addComputedColumnsToRowObject(table.columns.filter(isComputedColumn), newRowData);

        // The Data Editor always shows all rows, but when we're in the player
        // or showing the app in the builder, we have to filter.  The
        // Firestore owner query doesn't apply here.
        //
        // FIXME: Also remove owned rows when they are changed and thereby
        // changing owner.
        if (this._forDataEditor || this.isRowOwnedByUser(newRowData, this._helper.getAppUserData())) {
            appData.appendItem(newRowData);
            this._addedRows.push([newRowData, rowID]);
        }

        return newRowData;
    }

    public setColumnsInRow(rowIndex: RowIndex, values: ValuesAndDatas<AppOther, AppEnvironment>): void {
        const maybeRow = this.findRow(rowIndex);
        if (maybeRow === undefined) return undefined;

        const { row, rowObject } = maybeRow;

        const toUpdate: Data[] = [];

        for (const [columnName, columnValue] of toPairs(values.datas)) {
            const existingData = rowObject[columnName];
            if (existingData === undefined) {
                if (row instanceof Data) {
                    row.addPropertyWithoutUpdate(columnName, columnValue);
                    toUpdate.push(row);
                } else {
                    row.addProperty(columnName, columnValue);
                }
            } else {
                const { current } = columnValue;
                if (current !== LOADING) {
                    if (existingData instanceof Data) {
                        existingData.setWithoutUpdate(current);
                        toUpdate.push(existingData);
                    } else {
                        existingData.set(current);
                    }
                }
            }
        }

        for (const d of toUpdate) {
            d.setListenersDirty();
        }
    }

    public updateRowWithPartialData(rowIndex: RowIndex, updates: DocumentData): void {
        const maybeRow = this.findRow(rowIndex);
        if (maybeRow === undefined) return;
        const table = this._helper.getTable();
        if (table === undefined) return;

        updateRowData(this._db, maybeRow.row, updates, table, false);
    }

    public deleteRowAtIndex(rowIndex: RowIndex): void {
        const maybeRow = this.findRow(rowIndex);
        if (maybeRow === undefined) return undefined;

        maybeRow.appData.removeItemAtIndex(maybeRow.index);
    }

    public updateRows(update: (rowData: Data) => void): void {
        for (const rowData of this._appDataRows.values()) {
            update(rowData);
        }
        for (const [rowData] of this._addedRows) {
            update(rowData);
        }
    }

    public removeComputedColumns(columnsToDelete: readonly string[], alwaysSetDirty: boolean): void {
        this.updateRows(rowData => {
            let didChange = false;
            for (const c of columnsToDelete) {
                if (rowData.removePropertyWithoutUpdate(c)) {
                    didChange = true;
                }
            }
            if (alwaysSetDirty) {
                // dataRowStore.addComputedColumnsToRowObject(newComputedColumns, rowData);
                rowData.setListenersDirty();
            } else if (didChange) {
                rowData.setListenersDirty();
            }
        });
    }

    private getDataRow(
        id: string,
        appUserData: AppUserData | undefined = this._helper.getAppUserData()
    ): Data | undefined {
        const row = this._appDataRows.get(id);
        if (row === undefined || !this.isRowIncluded(row, appUserData)) return undefined;
        return row;
    }

    public updateRowIfPresent(id: string, updates: DocumentData): void {
        const table = this._helper.getTable();
        if (table === undefined) return;

        const rowData = this.getDataRow(id);
        if (rowData === undefined) return;

        updateRowData(this._db, rowData, updates, table, true);
    }

    private setDataRow(id: string, appRowData: Data): void {
        this._appDataRows.set(id, appRowData);
    }

    public deleteDataRow(id: string): void {
        this._appDataRows.delete(id);
    }

    public clear(): void {
        // FIXME: This doesn't delete added rows?
        this._appDataRows.clear();
    }

    public addComputedColumnsToRowObject(computedColumns: readonly TableColumn[], rowData: Data): void {
        for (const column of computedColumns) {
            const vp = this.makeComputedColumnForRow(rowData, column);
            if (vp === undefined) continue;

            rowData.addPropertyWithoutUpdate(column.name, vp);
        }
    }

    private makeDataForRowObject(
        obj: DocumentData,
        userData: DocumentData | undefined,
        table: TableGlideType
    ): Data | undefined {
        const parent = this.getAppData();
        if (parent === undefined) return undefined;

        const rowData = new RowData({}, parent);
        updateRowData(this._db, rowData, { ...obj, ...userData }, table, false);
        // this.addComputedColumnsToRowObject(computedColumns, rowData);
        return rowData;
    }
}

interface DataRowStores {
    readonly playerVP: DataRowStore | undefined;
    readonly builderVP: DataRowStore | undefined;
    readonly playerNCM: ComputationModelDataRowStore | undefined;
    readonly builderNCM: ComputationModelDataRowStore | undefined;
}

function getAllDataRowStores(drs: DataRowStores): readonly DataRowStore[] {
    const stores: DataRowStore[] = [];
    if (drs.playerVP !== undefined) {
        stores.push(drs.playerVP);
    }
    if (drs.builderVP !== undefined) {
        stores.push(drs.builderVP);
    }
    if (drs.playerNCM !== undefined) {
        stores.push(drs.playerNCM);
    }
    if (drs.builderNCM !== undefined) {
        stores.push(drs.builderNCM);
    }
    return stores;
}

// * Whenever we see a new user-agnostic row, we try to assign unassigned user-specific
//   documents. We combine whichever documents match, and then process them via the
//   durable storage controller, and store the result.

class TableDataHandler {
    private _gotInitialResults = false;
    // This is the snapshot version, if we have it
    private _defaultRowVersion: number | undefined;
    // Indexed by row ID
    private _tableData = new Map<string, RowDocumentDatas>();

    // User-specific documents that we haven't assigned to rows yet we keep
    // indexed by keyColumnName, keyColumnValue.
    private readonly _unassignedUserData = new Map<string, Map<unknown, UserSpecificRow[]>>();
    // For user-specific documents that we have already assigned to rows, just keep one
    // combined object per row, indexed by row index, with its last version.
    private readonly _assignedUserData = new Map<BaseRowIndex, [DocumentData, number]>();

    private _deletedRowIndexes: Set<BaseRowIndex> = new Set();
    // See updateWithDiffResults: 'false' means we have not established any version
    // for the tombstones yet, and we also haven't received any tombstones either.
    // So we're completely in the dark as to what is and isn't deleted.
    private _deletedRowsVersion: number | false = false;
    // We use this to keep track of the rows we have deleted locally.  It maps
    // from row index to the confirmed version of the deletion, or `undefined`
    // if it's not confirmed yet, in which case we always consider it deleted.
    private readonly _locallyDeletedRowIndexes: Map<BaseRowIndex, number | undefined> = new Map();

    private _numRowsUsed: number = 0;
    private _didSetUserTableData: boolean = false;

    private _lastUserSpecificRows: readonly UserSpecificRow[] | undefined;

    // See updateWithDiffResults: these are needed to reconsider which rows
    // were truly deleted when we start off with an indeterminate deletedRowsVersion.
    // "Indeterminate" meaning either 'false' or 'Number.MAX_SAFE_INTEGER'.
    // In the first case, we haven't loaded the deleted row index set yet.
    // In the second case, we have, but it doesn't have a version yet.
    private readonly publicIndeterminateReaddedResults: DiffResult[] = [];
    private readonly privateIndeterminateReaddedResults: DiffResult[] = [];

    constructor(
        private readonly _db: Database,
        private readonly _isBuilder: boolean,
        private readonly _helper: TableDataHelper,
        private readonly _durableStorageController: DurableStorageController,
        private readonly _dataRowStores: DataRowStores
    ) {}

    public getRowsUsed(): number {
        return this._numRowsUsed;
    }

    private getUserSpecificDataForRow(
        rowIndex: BaseRowIndex,
        rowData: DocumentData
    ): { data: DocumentData; version: number } | undefined {
        const fromRowIndex = this._assignedUserData.get(rowIndex);
        if (fromRowIndex !== undefined) {
            return { data: fromRowIndex[0], version: fromRowIndex[1] };
        }

        const table = this._helper.getTable();
        if (table === undefined) return undefined;

        const results: UserSpecificRow[] = [];
        for (const key of Object.keys(rowData)) {
            const valueIndex = this._unassignedUserData.get(key);
            if (valueIndex === undefined) continue;
            const value = rowData[key];
            const rowArray = valueIndex.get(value);
            if (rowArray === undefined) continue;
            results.push(...rowArray);
            valueIndex.delete(value);
        }

        if (results.length === 0) return undefined;

        // `liftUserSpecificData` will take the first value for each column
        // that it finds, so we need to sort by highest row version first.
        // https://github.com/quicktype/glide/issues/10713
        results.sort((l, r) => -compareUserSpecificRowsForApplication(l, r));
        const version = results[0].$rowVersion ?? Number.MIN_SAFE_INTEGER;

        const data = liftUserSpecificData(table, results);

        this._assignedUserData.set(rowIndex, [data, version]);

        return { data, version };
    }

    // When the user-specific documents update, we reassign the updated documents to
    // rows, run them through the durable storage controller, and store the result.
    //
    // If `results` is `undefined, we'll use the last results.
    private setUserTableDataDirect = (results: readonly UserSpecificRow[] | undefined, reset: boolean): void => {
        if (results === undefined) {
            results = this._lastUserSpecificRows;
            if (results === undefined) return;
        }

        const table = this._helper.getTable();
        if (table === undefined) return;
        const tableName = getTableName(table);

        this._assignedUserData.clear();
        this._unassignedUserData.clear();

        const mapping = makeColumnMappingForJSONFromDocumentData(this._helper.getFieldNameCache(), table);

        for (const userDataRow of results) {
            const { keyColumnName, keyColumnValue, data } = userDataRow;
            const converted = convertFieldNamesToColumnNamesWithMapping(this._db.convertFromDocument(data), mapping);

            let valueIndex = this._unassignedUserData.get(keyColumnName);
            if (valueIndex === undefined) {
                valueIndex = new Map();
                this._unassignedUserData.set(keyColumnName, valueIndex);
            }

            let rowArray = valueIndex.get(keyColumnValue);
            if (rowArray === undefined) {
                rowArray = [];
                valueIndex.set(keyColumnValue, rowArray);
            }
            rowArray.push({ ...userDataRow, data: converted });
        }

        let resetUserData: DocumentData | undefined;
        if (reset) {
            this._durableStorageController.resetUserSpecificData();
            resetUserData = objectWithUndefinedProperties(table.columns.map(c => c.name));
        }

        for (const [id, rowDocumentDatas] of this._tableData.entries()) {
            const tableDocument = combineRowDocumentDatas(rowDocumentDatas);
            const rowIndex = getRowIndexForDocumentData(tableDocument);
            if (rowIndex === undefined) {
                logError("No row index", table, id);
                continue;
            }

            const userData = this.getUserSpecificDataForRow(rowIndex, tableDocument);
            let userUpdates: DocumentData | undefined;

            if (userData === undefined) {
                if (resetUserData === undefined) {
                    userUpdates = this._durableStorageController.handleUserSpecificDataDeletion(tableName, rowIndex);
                    if (userUpdates === undefined) continue;
                } else {
                    userUpdates = resetUserData;
                }
            } else {
                userUpdates = this._durableStorageController.handleUserSpecificDocumentUpdate(
                    tableName,
                    rowIndex,
                    userData.version,
                    userData.data
                );
            }

            for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
                dataRowStore.updateRowIfPresent(id, userUpdates);
            }
        }

        this._lastUserSpecificRows = results;
    };

    private setUserTableDataDebounced = debounce(this.setUserTableDataDirect, 10000);

    public setUserTableData(results: readonly UserSpecificRow[]): void {
        if (!this._didSetUserTableData || this._isBuilder) {
            this.setUserTableDataDirect(results, false);
            this._didSetUserTableData = true;
        } else {
            this.setUserTableDataDebounced(results, false);
        }
    }

    private setTableData(id: string, data: DocumentData, isPrivate: boolean): void {
        const entry = this._tableData.get(id);
        const altered = setRowDocumentData(entry, data, isPrivate);
        this._tableData.set(id, altered);
    }

    // returns whether all data with this `id` was deleted
    private deleteTableData(id: string, isPrivate: boolean): boolean {
        const entry = this._tableData.get(id);
        if (entry === undefined) return true;
        const altered = deleteRowDocumentData(entry, isPrivate);
        if (altered === undefined) {
            this._tableData.delete(id);
            return true;
        } else {
            this._tableData.set(id, altered);
            return false;
        }
    }

    public updateWithDiffResults(results: DiffResults, isPrivate: boolean): void {
        const table = this._helper.getTable();
        if (table === undefined) {
            logError("No table");
            return;
        }
        const tableName = getTableName(table);

        // See below: we may have to reconsider whether a row is actually deleted
        // when we start with an indeterminate deleted row version, meaning we haven't
        // loaded any of the deleted rows, or we have, and they don't yet have a version.
        const indeterminateReaddedResults = isPrivate
            ? this.privateIndeterminateReaddedResults
            : this.publicIndeterminateReaddedResults;
        const previouslyIndeterminateResults = indeterminateReaddedResults.splice(
            0,
            indeterminateReaddedResults.length
        );
        const processedResults =
            previouslyIndeterminateResults.length === 0 ? results : [...previouslyIndeterminateResults, ...results];
        const sortedResults = sortByRowIndex(processedResults, getRowIndexForDocumentData);

        let rowsLeft = this._helper.getRowsLeftInQuota();
        rowsLeft += sortedResults.filter(({ kind }) => kind === "removed").length;
        // we always allow reading at least 10 rows in each table
        rowsLeft = Math.max(rowsLeft, 10 - this.getRowsUsed());

        const mapping = makeColumnMappingForJSONFromDocumentData(this._helper.getFieldNameCache(), table);

        let reprocessUserSpecificData = false;

        for (const { kind, id, data } of sortedResults) {
            switch (kind) {
                // We treat these the same because I suspect that under some conditions
                // (connection loss and/or clearance of local storage) we might get
                // "added" events for documents we already have.
                case "added":
                case "modified":
                    const converted = convertFieldNamesToColumnNamesWithMapping(
                        this._db.convertFromDocument(data),
                        mapping
                    );

                    // Data without a row index of any kind (both numeric and key-value based)
                    // should never occur under correct conditions.
                    const rowIndex = getRowIndexForDocumentData(converted);
                    if (rowIndex === undefined) continue;
                    const rowVersion =
                        getRowVersionForDocumentData(converted) ?? this._defaultRowVersion ?? Number.MIN_SAFE_INTEGER;

                    this.setTableData(id, converted, isPrivate);

                    // Deletion tombstones are generally versioned. For any row data we receive, if
                    // the row index for the row data is in the deletion tombstone set,
                    // 1. The deletion tombstone set wins if its version is greater than or equal to than the row.
                    // 2. Otherwise, the row wins.
                    // This all assumes that we have a definitive version for the deletion tombstones.
                    if (isBaseRowIndex(rowIndex) && this.isRowIndexDeletedAtVersion(rowIndex, rowVersion)) {
                        // We don't always have a definitive version for the deletion tombstones, though.
                        // It is possible that we have rows but not a deleted row set yet.
                        // Assuming that we do have a deleted row set, while all changes from the sheet mirror
                        // now result in a versioned deletion tombstone set, there are still valid mirrored sheets
                        // that don't have this version.
                        // In this case, we internally treat the set as having the maximum possible version.
                        // Whenever this happens, we _think_ that the rows are going to stay deleted.
                        //
                        // We'll thus need to revisit this change entry in two circumstances:
                        // 1. We received row documents before we established which ones were deleted.
                        //    We consider every row to be deleted until we receive the actual set of deleted rows,
                        //    with the exception of snapshot rows, which are not deleted until proven otherwise.
                        // 2. An update occurs that revives a deleted row, but the update notification occurs
                        //    _before_ the deleted row notification that establishes a definitive version.
                        // Unless we save these rows for future processing, we'll never get them back.
                        //
                        // Note that we don't do this for snapshots: we just take whatever the snapshot says
                        // as a given until the table listeners are established. This is a performance
                        // optimization that is close enough in terms of consistency.
                        if (this.isDeletedRowVersionIndeterminate()) {
                            // Note that we push `data`, not `converted`, because indeterminate data goes
                            // through conversion (again) above.
                            indeterminateReaddedResults.push({ kind, id, data });
                        }
                        continue;
                    }

                    const updates = this._durableStorageController.handleUserAgnosticDocumentUpdate(
                        tableName,
                        rowIndex,
                        rowVersion,
                        converted
                    );
                    const userSpecificData = this.getUserSpecificDataForRow(rowIndex, converted);
                    let userDataUpdates: DocumentData | undefined;
                    if (userSpecificData !== undefined) {
                        userDataUpdates = this._durableStorageController.handleUserSpecificDocumentUpdate(
                            tableName,
                            rowIndex,
                            userSpecificData.version,
                            userSpecificData.data
                        );
                    }

                    let didConsumeRow = false;
                    for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
                        // We're assuming here that there's only one document
                        // ID per row index, which is wrong if we can't load
                        // the snapshot.  See
                        // https://github.com/quicktype/glide/pull/9465
                        const result = dataRowStore.updateRow(
                            id,
                            kind === "added",
                            rowsLeft > 0,
                            updates,
                            userDataUpdates,
                            converted,
                            userSpecificData?.data
                        );
                        if (result === undefined) continue;

                        if (result.reprocessUserSpecificData) {
                            reprocessUserSpecificData = true;
                        }
                        if (result.didAddRow) {
                            didConsumeRow = true;
                        }
                    }
                    if (didConsumeRow) {
                        rowsLeft -= 1;
                    }
                    break;

                case "removed":
                    // When rows move between non-owned and
                    // owned, we get these diffs. Does this have to
                    // go through the durable storage controller?
                    if (this.deleteTableData(id, isPrivate)) {
                        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
                            dataRowStore.deleteDataRow(id);
                        }
                    }
                    break;

                default:
                    return assertNever(kind);
            }
        }

        if (reprocessUserSpecificData) {
            this.setUserTableDataDirect(undefined, true);
        }
    }

    private isDeletedRowVersionIndeterminate(): boolean {
        return this._deletedRowsVersion === false || this._deletedRowsVersion === Number.MAX_SAFE_INTEGER;
    }

    private isRowIndexDeletedAtVersion(rowIndex: BaseRowIndex, version: number): boolean {
        if (this._locallyDeletedRowIndexes.has(rowIndex)) {
            const deletedVersion = this._locallyDeletedRowIndexes.get(rowIndex);
            if (deletedVersion === undefined) {
                // The backend has not confirmed the deletion yet, so we have
                // to consider the row deleted.
                return true;
            }

            // We have a confirmed version for the deletion, so it's possible
            // that after that version the row comes back again.  If we
            // haven't reached the confirmed version yet, we consider the row
            // deleted (since it happened locally).  Anything that happens
            // after that version is handled by the fallthrough case below.
            if (deletedVersion >= version) {
                return true;
            }
        }

        // We receive a defined _deletedRowsVersion whenever we load a snapshot,
        // and only whenever we load a snapshot. We want the snapshot data to be
        // considered non-deleted for as long as we're waiting for the initial
        // deleted rows, but only the snapshot data should be considered non-deleted
        // until we have an initial set of deleted rows.
        if (this._deletedRowsVersion === false) {
            return (this._defaultRowVersion ?? Number.MIN_SAFE_INTEGER) < version;
        }
        if (version > this._deletedRowsVersion) return false;
        return this._deletedRowIndexes.has(rowIndex);
    }

    public setDeletedRowIndexes(rowIndexes: readonly BaseRowIndex[], version: number): void {
        this._deletedRowIndexes = new Set(rowIndexes);
        this._deletedRowsVersion = version;
    }

    public updateWithSnapshotResults(
        results: ReadonlyArray<DocumentDataWithID>,
        version: number | undefined,
        force: boolean = false
    ): void {
        if (this._gotInitialResults && !force) return;
        this._gotInitialResults = true;
        if (version !== undefined) {
            this._defaultRowVersion = version;
        }

        if (this._tableData.size > 0 && !force) return;

        if (force) {
            this._durableStorageController.purgeConfirmedEntries();
        }
        this.updateWithDiffResults(
            results.map(r => ({ ...r, kind: "added" })),
            false
        );
    }

    private deleteOutdatedRow(id: string): void {
        this._tableData.delete(id);
        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
            dataRowStore.deleteDataRow(id);
        }
    }

    private getRowVersion(datas: RowDocumentDatas): number | undefined {
        return getForRowDocumentDatas(datas, getRowVersionForDocumentData) ?? this._defaultRowVersion;
    }

    private deleteOldRows(sortedEntries: TableDataEntries): TableDataEntries {
        const cleanEntries: [string, RowDocumentDatas][] = [];

        // All `entries` have the same row index.  Find the one with the
        // highest version, push it to `cleanEntries`, and get rid of all
        // the other rows for good.
        const processEntries = (isDeleted: boolean, entries: TableDataEntries): void => {
            assert(entries.length > 0);

            let maxVersion: number | undefined;
            // We keep the entry with this index.  This will typically
            // be the entry with the highest version, but we might end
            // up not preserving one at all, because the row should be
            // deleted, or preserving the first one because none of them
            // had versions.
            let preserveIndex: number | undefined;
            for (let i = 0; i < entries.length; i++) {
                const version = this.getRowVersion(entries[i][1]);
                if (version === undefined) continue;

                if (maxVersion === undefined || version > maxVersion) {
                    maxVersion = version;
                    preserveIndex = i;
                }
            }

            if (preserveIndex === undefined) {
                // This should only happen if no entries have row versions,
                // which shouldn't happen, but who knows?
                preserveIndex = 0;
            }

            if (!isDeleted || (maxVersion !== undefined && maxVersion > this._deletedRowsVersion)) {
                cleanEntries.push(entries[preserveIndex]);
            } else {
                // This row has been deleted in a version at least as recent as
                // the row version, so we don't push it, and in the loop below
                // all documents get deleted from memory.
                preserveIndex = undefined;
            }

            for (let i = 0; i < entries.length; i++) {
                if (i === preserveIndex) continue;
                this.deleteOutdatedRow(entries[i][0]);
            }
        };

        const numEntries = sortedEntries.length;

        // Find groups of entries with the same row index.
        // We're manually incrementing `i` in this loop.
        for (let i = 0; i < numEntries; ) {
            const entry = sortedEntries[i];
            const rowIndex = getRowIndex(entry[1]);
            const rowVersion = this.getRowVersion(entry[1]);
            const deletedByIndexAndVersion =
                rowIndex !== undefined && rowVersion !== undefined
                    ? this.isRowIndexDeletedAtVersion(rowIndex, rowVersion)
                    : undefined;

            if (rowIndex === undefined) {
                cleanEntries.push(entry);
                i++;
                continue;
            }

            let j: number;
            for (j = i + 1; j < numEntries; j++) {
                if (getRowIndex(sortedEntries[j][1]) !== rowIndex) {
                    break;
                }
            }

            const isDeleted = deletedByIndexAndVersion ?? this._deletedRowIndexes.has(rowIndex);
            // This is a shortcut to avoid `processEntries` in almost all cases.
            if (!isDeleted && j === i + 1) {
                cleanEntries.push(entry);
                i++;
                continue;
            }

            // These all have the same row index
            processEntries(isDeleted, sortedEntries.slice(i, j));
            i = j;
        }

        return cleanEntries;
    }

    public updateAppWithTableData(): void {
        const numRowsUsed: number[] = [];
        // `deleteOldRows` has the side effect of potentially deleting rows
        // from this object and the data row stores.
        const sortedEntries = this.deleteOldRows(sortByRowIndex(this._tableData.entries(), ([, x]) => getRowIndex(x)));

        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
            const numRows = dataRowStore.setAppDataForSortedEntries(sortedEntries);
            if (numRows === undefined) continue;

            numRowsUsed.push(numRows);
        }

        if (numRowsUsed.length > 0) {
            this._numRowsUsed = Math.max(...numRowsUsed);
        }
    }

    // We're returning the one for the app, which is from the first store.
    public addRow(
        values: ValuesAndDatas<AppOther, AppEnvironment>,
        rowID: string | undefined
    ): ValueProducer | undefined {
        let row: ValueProducer | undefined;
        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
            const newRow = dataRowStore.addRowWithValues(values, rowID);
            if (row === undefined) {
                row = newRow;
            }
        }
        return row;
    }

    public addComputedColumnsToData(data: Data): void {
        const table = this._helper.getTable();
        if (table === undefined) return;

        this._dataRowStores.playerVP?.addComputedColumnsToRowObject?.(table.columns.filter(isComputedColumn), data);
    }

    public setColumnsInRow(rowIndex: RowIndex, values: ValuesAndDatas<AppOther, AppEnvironment>): void {
        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
            dataRowStore.setColumnsInRow(rowIndex, values);
        }
    }

    public updateRowWithPartialData(rowIndex: RowIndex, doc: DocumentData): void {
        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
            dataRowStore.updateRowWithPartialData(rowIndex, doc);
        }
    }

    public deleteRowAtIndex(rowIndex: RowIndex): void {
        const baseRowIndex = isBaseRowIndex(rowIndex) ? rowIndex : rowIndex.rowIndexHint;
        if (baseRowIndex !== undefined && !this._locallyDeletedRowIndexes.has(baseRowIndex)) {
            this._locallyDeletedRowIndexes.set(baseRowIndex, undefined);
        }

        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
            dataRowStore.deleteRowAtIndex(rowIndex);
        }
    }

    public confirmDeletedRow(rowIndex: BaseRowIndex, serial: number): void {
        if (this._locallyDeletedRowIndexes.has(rowIndex)) {
            this._locallyDeletedRowIndexes.set(rowIndex, serial);
        }
    }

    public clearUserData(): void {
        this.setUserTableDataDirect([], true);
    }

    public reset(): void {
        this._tableData.clear();
        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
            dataRowStore.clear();
        }
        // FIXME: Why does this not clear the user rows and the added rows?
    }

    public resetChangedComputedColumns(changedColumns: readonly string[]): void {
        const table = this._helper.getTable();
        if (table === undefined) return;

        const newComputedColumns = mapFilterUndefined(changedColumns, c => getTableColumn(table, c));

        for (const dataRowStore of getAllDataRowStores(this._dataRowStores)) {
            dataRowStore.removeComputedColumns(changedColumns, newComputedColumns.length > 0);
        }
    }

    public getTableKeeper(forBuilder: boolean): Handler | undefined {
        if (forBuilder) {
            return this._dataRowStores.builderNCM;
        } else {
            return panic("FIXME: implement");
        }
    }
}

function didEmailOwnersColumnsChange(
    oldTables: readonly TableGlideType[],
    newTables: readonly TableGlideType[]
): boolean {
    for (const table of oldTables) {
        const newTable = findTable(newTables, getTableName(table));
        if (newTable === undefined) continue;
        if (!areSetsEqual(getEmailOwnersColumnNames(table), getEmailOwnersColumnNames(newTable))) {
            return true;
        }
    }
    return false;
}

type ChangedComputedColumns = ReadonlyMap<TableName, readonly string[]>;

// If there is a new computed column in an existing table, or an existing
// computed column that is now different, or a computed column that was deleted,
// we count that as changed.  We already handle computed columns in new tables
// correctly, since we construct new row objects for those tables.
function getChangedComputedColumns(
    oldTables: readonly TableGlideType[],
    newTables: readonly TableGlideType[]
): ChangedComputedColumns | undefined {
    const changedColumnsForTables: Map<TableName, readonly string[]> = new Map();

    for (const table of newTables) {
        const tableName = getTableName(table);
        const oldTable = findTable(oldTables, tableName);
        if (oldTable === undefined) continue;

        const changedColumns: string[] = [];

        for (const c of table.columns) {
            if (!isComputedColumn(c)) continue;

            const oldColumn = getTableColumn(oldTable, c.name);
            if (oldColumn === undefined || !deepEqual(c.formula, oldColumn.formula, { strict: true })) {
                changedColumns.push(c.name);
            }
        }

        for (const c of oldTable.columns) {
            if (!isComputedColumn(c)) continue;

            if (getTableColumn(table, c.name) === undefined) {
                changedColumns.push(c.name);
            }
        }

        if (changedColumns.length > 0) {
            changedColumnsForTables.set(tableName, changedColumns);
        }
    }

    if (changedColumnsForTables.size === 0) return undefined;
    return changedColumnsForTables;
}

// This exists entirely for testing purposes.
export type FirestoreDataStoreCallbacks = {
    onUpdateWithDiffResults?: () => void;
    onUpdateWithUserSpecificData?: () => void;
};

class DocumentsFirestoreHandlerImpl implements DocumentsFirestoreHandler, TableFetcher, TableMutationHandler {
    private readonly _listeners = new Map<string, FirestoreTableListener>();
    private readonly _refs: RefValueProducer[] = [];
    private readonly _tableDataHandlers = new Map<string, TableDataHandler>();
    // `false` means we tried to load but failed
    private _snapshot: DataSnapshot | undefined | false;
    // FIXME: This shouldn't be publicly accessible, or at least not
    // writable.
    public appUserRoles: readonly string[] = [];
    private _appUserRolesUnlisten: (() => void) | undefined;

    constructor(
        public readonly appFacilities: AppFacilities,
        public readonly database: Database,
        private readonly _tables: ReadonlyMap<string, TableGlideType>,
        private readonly _playerTableKeeperStore: TableKeeperStore<ComputationModelDataRowStore>,
        private readonly _builderTableKeeperStore: TableKeeperStore<ComputationModelDataRowStore>,
        private readonly _dataRowStores: Map<string, DataRowStores>,
        private readonly _fieldNameCache: FieldNameCache,
        private readonly _appDataForTables: DataForTables,
        private readonly _builderDataForTables: DataForTables | undefined,
        public readonly appID: string,
        // FIXME: This shouldn't be publicy settable
        public appUserID: string | undefined,
        private _appUserEmail: string | undefined,
        private readonly _isBuilder: boolean,
        private readonly _needsOCM: boolean,
        private readonly _needsNCM: boolean,
        private readonly _makeComputedColumn: (
            tableName: TableName,
            columnName: string,
            formula: Formula,
            isBuilder: boolean,
            forDataEditor: boolean
        ) => EnvironmentBoundEvaluable | undefined,
        private readonly _getRowQuota: () => number,
        private readonly _durableStorageController: DurableStorageController,
        private readonly _callbacks: FirestoreDataStoreCallbacks
    ) {
        void this.startLoading();
    }

    private notifyListenerOfSnapshot(listener: FirestoreTableListener): void {
        if (this._snapshot === undefined) return undefined;

        if (this._snapshot === false) {
            listener.snapshotUnavailable();
        } else {
            listener.snapshotLoaded(this._snapshot.version, this._snapshot.data[getTableName(listener.table).name]);
        }
    }

    // FIXME: We don't seem to wait with querying data, or filtering in the
    // builder, for the roles to arrive, which seems very racy?  If we get the
    // roles before we query/filter, everything is fine, but if it's the other
    // way around, we end up with incorrectly filtered rows, or too narrow a
    // query.
    //
    // https://github.com/quicktype/glide/issues/7541
    private listenToAppUserRoles(): void {
        if (this._appUserRolesUnlisten !== undefined) {
            this._appUserRolesUnlisten();
            this._appUserRolesUnlisten = undefined;
        }

        const { appUserID } = this;
        if (appUserID === undefined) return;

        const path = makeAppUsersForAppPath(this.appID);
        this._appUserRolesUnlisten = this.database.listenToDocument(
            path,
            appUserID,
            data => {
                const { roles } = makeAppUserForAppFromDocument(data);
                logInfo("app user document", roles);

                if (this.appUserID !== appUserID) return;

                if (areEqual(new Set(roles), new Set(this.appUserRoles))) return;
                this.appUserRoles = roles;

                this.unlistenUserSpecifics();
                this.listenUserSpecifics();
            },
            error => {
                logError("Error listening to app user roles", error);
            }
        );
    }

    private async startLoading(): Promise<void> {
        await frontendTrace("tryLoadDataSnapshot", { is_builder: this._isBuilder }, async extraFields => {
            this._snapshot = withDefault<DataSnapshot | false>(
                await this.appFacilities.loadDataSnapshot(this.appID),
                false
            );

            extraFields.success = this._snapshot !== false;
        });

        logInfo("snapshot", this._snapshot);

        // Notifying the listeners will modify stuff in the component model
        // which in turn can cause more requests for table data, which adds
        // more listeners.  `Map.values()` is not built for that - it will give
        // incoherent results if the Map is modified while being iterated
        // over.
        const listeners = Array.from(this._listeners.values());
        for (const listener of listeners) {
            this.notifyListenerOfSnapshot(listener);
        }

        this.listenToAppUserRoles();
    }

    private getListener(table: TableGlideType): FirestoreTableListener {
        const tableName = getTableName(table);
        const documentID = documentIDForTableName(tableName);
        let listener = this._listeners.get(documentID);
        if (listener === undefined) {
            // FIXME: We really shouldn't check the table name for whether
            // it's a native table, but instead look into the signed snapshot
            // response for whether the table name is in there.
            if (getFeatureSetting("nativeTableSnapshots") && getNativeTableIDFromTableName(tableName) !== undefined) {
                listener = new FirestoreNativeTableListener(
                    this,
                    table,
                    this._isBuilder,
                    this._durableStorageController
                );
            } else {
                listener = new FirestoreGoogleSheetsTableListener(
                    this,
                    table,
                    this._isBuilder,
                    this._durableStorageController
                );
            }
            this._listeners.set(documentID, listener);
            this.notifyListenerOfSnapshot(listener);
        }
        return listener;
    }

    public onPublishedAppDataUpdate(
        ownerColumnsChanged: boolean,
        changedComputedColumns: ChangedComputedColumns | undefined
    ): void {
        const updateDataHandlers = (withReset: boolean) => {
            for (const dataHandler of this._tableDataHandlers.values()) {
                if (withReset) {
                    dataHandler.reset();
                }
                dataHandler.updateAppWithTableData();
            }
        };

        if (this._isBuilder && changedComputedColumns === undefined) {
            updateDataHandlers(false);
            return;
        }

        if (!ownerColumnsChanged) {
            if (changedComputedColumns !== undefined) {
                for (const [tableName, changedColumns] of changedComputedColumns.entries()) {
                    const documentID = documentIDForTableName(tableName);
                    const handler = this._tableDataHandlers.get(documentID);
                    if (handler === undefined) continue;
                    handler.resetChangedComputedColumns(changedColumns);
                }
            }

            return;
        }

        for (const listener of this._listeners.values()) {
            listener.unlisten();
        }

        updateDataHandlers(true);

        for (const listener of this._listeners.values()) {
            listener.listenToTable(true);
        }
    }

    private unlistenUserSpecifics(): void {
        for (const listener of this._listeners.values()) {
            listener.unlistenUserSpecifics();
        }
        for (const handler of this._tableDataHandlers.values()) {
            handler.clearUserData();
        }
    }

    private listenUserSpecifics(): void {
        for (const listener of this._listeners.values()) {
            listener.listenToTable(false);
        }
    }

    public setAppUser(appUserID: string, appUserEmail: string | undefined): void {
        assert(this.appUserID !== appUserID || this._appUserEmail !== appUserEmail);

        if (this.appUserID !== undefined) {
            // We're already checking this in the caller, so technically this
            // is superfluous.
            if (this.appUserID === appUserID && !this._isBuilder) {
                return;
            }

            if (this.appUserID !== appUserID) {
                this.unlistenUserSpecifics();
            }
        }

        if (this.appUserID === appUserID && this._isBuilder) {
            // Only the email has changed.  In the builder we have to
            // re-filter to account for row owners and admins.
            assert(this._appUserEmail !== appUserEmail);

            this._appUserEmail = appUserEmail;

            for (const [documentID, dataHandler] of this._tableDataHandlers.entries()) {
                const table = defined(this._tables.get(documentID));
                if (tableHasRowOwners(table)) {
                    dataHandler.updateAppWithTableData();
                }
            }

            return;
        }

        this.appUserID = appUserID;
        this._appUserEmail = appUserEmail;

        this.listenUserSpecifics();

        this.listenToAppUserRoles();
    }

    private getRowsUsed(): number {
        let total = 0;
        for (const n of Array.from(this._tableDataHandlers.values()).map(h => h.getRowsUsed())) {
            total += n;
        }
        return total;
    }

    public getAppUserData(): AppUserData | undefined {
        if (this._appUserEmail === undefined) return undefined;
        return { email: normalizeEmailAddress(this._appUserEmail), roles: new Set(this.appUserRoles) };
    }

    private getTableDataHandler(tableName: TableName): TableDataHandler {
        const documentID = documentIDForTableName(tableName);
        let handler = this._tableDataHandlers.get(documentID);
        if (handler === undefined) {
            const helper: TableDataHelper = {
                getTable: () => this.getTable(tableName),
                getFieldNameCache: () => this._fieldNameCache,
                getData: forBuilder => this.getData(tableName, forBuilder),
                makeComputedColumn: (columnName, spec, isBuilder, forDataEditor) =>
                    this._makeComputedColumn(tableName, columnName, spec, isBuilder, forDataEditor),
                getRowsLeftInQuota: () => this._getRowQuota() - this.getRowsUsed(),
                getAppUserData: () => this.getAppUserData(),
            };

            let dataRowStores = this._dataRowStores.get(documentID);
            if (dataRowStores === undefined) {
                // We need to keep these when we restart from snapshots.
                const stores: Writable<DataRowStores> = {
                    playerVP: undefined,
                    builderVP: undefined,
                    playerNCM: undefined,
                    builderNCM: undefined,
                };
                if (this._needsOCM) {
                    stores.playerVP = new ValueProducerDataRowStore(
                        this.database,
                        helper,
                        this._isBuilder,
                        false,
                        this._isBuilder
                    );
                    if (this._isBuilder) {
                        stores.builderVP = new ValueProducerDataRowStore(
                            this.database,
                            helper,
                            this._isBuilder,
                            true,
                            false
                        );
                    }
                }
                if (this._needsNCM) {
                    stores.playerNCM = this._playerTableKeeperStore.getTableKeeperForTable(tableName);
                }
                if (this._isBuilder) {
                    stores.builderNCM = this._builderTableKeeperStore.getTableKeeperForTable(tableName);
                }
                dataRowStores = stores;
                this._dataRowStores.set(documentID, dataRowStores);
            }

            handler = new TableDataHandler(
                this.database,
                this._isBuilder,
                helper,
                this._durableStorageController,
                dataRowStores
            );
            this._tableDataHandlers.set(documentID, handler);
        }
        return handler;
    }

    private getTableDataHandlerIfTableExists(tableName: TableName): TableDataHandler | undefined {
        const table = this.getTable(tableName);
        if (table === undefined) return undefined;

        return this.getTableDataHandler(tableName);
    }

    public updateWithSnapshotResults(
        table: TableGlideType,
        results: readonly DocumentDataWithID[],
        version: number | undefined,
        force: boolean = false
    ): void {
        const tableName = getTableName(table);

        logInfo("updating initial", tableName, results.length);
        const handler = this.getTableDataHandler(tableName);
        handler.updateWithSnapshotResults(results, version, force);
        handler.updateAppWithTableData();
        this.updateRefs();
    }

    public updateWithUserSpecificColumns(table: TableGlideType, results: readonly DocumentDataWithID[]): void {
        const tableName = getTableName(table);
        const handler = this.getTableDataHandler(tableName);

        const decoded = mapFilterUndefined(results, r => {
            const d = userSpecificRowCodec.decode(r.data);
            if (isLeft(d)) return undefined;
            return d.right;
        });
        logInfo("updating user-specific table", tableName, results.length, decoded.length);
        handler.setUserTableData(decoded);
        // Once we have refs depending on favorites, we might have to update them
        // here.
        // this.updateWithDiffResults(tableName, []);
        const { onUpdateWithUserSpecificData } = this._callbacks;
        if (onUpdateWithUserSpecificData !== undefined) {
            onUpdateWithUserSpecificData();
        }
    }

    private async listenToUserSpecificColumns(table: TableGlideType): Promise<void> {
        this.getListener(table).listenToTable(false);
    }

    public fetchTableRows(tableName: TableName): void {
        const table = this.getTable(tableName);
        if (table === undefined) {
            logError("Table not found", tableName.name);
            return;
        }

        this.getListener(table).listenToTable(false);

        void this.listenToUserSpecificColumns(table);
    }

    private getTable(tableName: TableName): TableGlideType | undefined {
        const documentID = documentIDForTableName(tableName);
        return this._tables.get(documentID);
    }

    private getData(tableName: TableName, forBuilder: boolean): DataOrForwarder | undefined {
        const documentID = documentIDForTableName(tableName);
        const dataForTables = forBuilder ? this._builderDataForTables : this._appDataForTables;
        if (dataForTables === undefined) return undefined;
        return dataForTables[documentID];
    }

    private updateRefs(): void {
        for (const ref of this._refs) {
            ref.setDirty();
        }
    }

    public updateWithDiffResults(tableName: TableName, results: DiffResults, isPrivate: boolean): void {
        const handler = this.getTableDataHandler(tableName);

        handler.updateWithDiffResults(results, isPrivate);
        logInfo("updating data for table", tableName.name, isPrivate, results.length, results);
        handler.updateAppWithTableData();
        this.updateRefs();

        const { onUpdateWithDiffResults } = this._callbacks;
        if (onUpdateWithDiffResults !== undefined) {
            onUpdateWithDiffResults();
        }
    }

    public setDeletedRowIndexes(tableName: TableName, rowIndexes: readonly BaseRowIndex[], version: number): void {
        logInfo("deleted rows", tableName.name, rowIndexes, version);

        const handler = this.getTableDataHandler(tableName);

        handler.setDeletedRowIndexes(rowIndexes, version);

        // We may have just established the first known set of deleted rows,
        // or changed the version of deleted rows from Number.MAX_SAFE_INTEGER
        // to a definitive version. In both cases, we need to re-trigger the
        // diff result handler, which may have saved indeterminate changes for
        // future consideration.
        handler.updateWithDiffResults([], false);
        handler.updateWithDiffResults([], true);

        handler.updateAppWithTableData();
        this.updateRefs();
    }

    private makeRef(refValueProducer: ValueProducer, forBuilder: boolean): RefValueProducer {
        const dataForTables = forBuilder ? defined(this._builderDataForTables) : this._appDataForTables;
        const ref = new RefValueProducer(dataForTables, refValueProducer, this);
        this._refs.push(ref);
        return ref;
    }

    public fetchSingleLinkRow(link: ValueProducer, forBuilder: boolean): ValueProducer {
        return this.makeRef(link, forBuilder);
    }

    public fetchMultipleLinkRows(link: ValueProducer, forBuilder: boolean): ValueProducer {
        return this.makeRef(link, forBuilder);
    }

    public addRowToTable(
        tableName: TableName,
        values: ValuesAndDatas<AppOther, AppEnvironment>
    ): ValueProducer | undefined {
        const table = this.getTable(tableName);
        if (table === undefined) return;

        let rowID: string | undefined;
        const { rowIDColumn } = table;
        if (rowIDColumn !== undefined) {
            const maybeRowID = values.values[rowIDColumn];
            if (typeof maybeRowID === "string") {
                rowID = maybeRowID;
            }
        }

        const handler = this.getTableDataHandler(tableName);
        return handler.addRow(values, rowID);
    }

    public addComputedColumnsToData(tableName: TableName, rowData: Data): void {
        const handler = this.getTableDataHandlerIfTableExists(tableName);
        handler?.addComputedColumnsToData(rowData);
    }

    public setColumnsInRow(
        tableName: TableName,
        rowIndex: RowIndex,
        values: ValuesAndDatas<AppOther, AppEnvironment>
    ): void {
        const handler = this.getTableDataHandlerIfTableExists(tableName);
        handler?.setColumnsInRow(rowIndex, values);
    }

    public deleteRowAtIndex(tableName: TableName, rowIndex: RowIndex): void {
        const handler = this.getTableDataHandlerIfTableExists(tableName);
        handler?.deleteRowAtIndex(rowIndex);
    }

    public confirmDeletedRow(tableName: TableName, rowIndex: BaseRowIndex, serial: number): void {
        const handler = this.getTableDataHandler(tableName);
        handler?.confirmDeletedRow(rowIndex, serial);
    }

    public updateRowWithPartialData(tableName: TableName, rowIndex: RowIndex, data: DocumentData): void {
        const handler = this.getTableDataHandlerIfTableExists(tableName);
        handler?.updateRowWithPartialData(rowIndex, data);
    }

    public retire(): void {
        for (const listener of this._listeners.values()) {
            listener.unlisten();
        }
        this.unlistenUserSpecifics();
        this._appUserRolesUnlisten?.();
    }

    public resetFromFirestore(): void {
        for (const listener of this._listeners.values()) {
            listener.resetFromFirestore();
        }
    }
}

function storageControllerConstructor(
    accessors: DataStoreEnvironmentAccessors,
    deviceIDOverride: string | undefined,
    isBuilder: boolean
): DurableStorageController {
    if (getFeatureSetting("durableActionPosting")) {
        return new NonblockingDurableStorageController(accessors, deviceIDOverride, isBuilder);
    } else {
        return new DummyDurableStorageController(accessors, deviceIDOverride, isBuilder);
    }
}

interface ComputationModelData {
    computationModel: ComputationModelImpl | "requested" | undefined;
    readonly tableKeeperStore: TableKeeperStore<ComputationModelDataRowStore>;
    readonly observable: Watchable<ComputationModel | undefined>;
}

export class FirestoreDataStore implements DataStore {
    private readonly _appFacilities: AppFacilities;
    // FIXME: This should be optional because we shouldn't need it for NCM,
    // but we're using it to signal whether we've ever needed a table.
    private readonly _appDataForTables: DataForTables;
    private readonly _builderDataForTables: DataForTables | undefined;
    private _listeningToPublishedAppData: boolean = false;
    private _handler: DocumentsFirestoreHandlerImpl | undefined;
    private _tablesFetched: Set<string> = new Set();
    private _linksWithoutHandlers: Map<
        ValueProducer,
        [SettableForwardingValueProducer<AppOther, AppEnvironment>, boolean, boolean]
    > = new Map();
    private _tables: Map<string, TableGlideType> = new Map();
    private _dataRowStores: Map<string, DataRowStores> = new Map();

    private readonly _appID: string;
    private _appUserID: string | undefined;
    private _appUserEmail: string | undefined;
    private _appEnvironment: MinimalAppEnvironment | undefined;
    private _appComputedColumns: Map<string, Map<string, EnvironmentBoundEvaluable>> = new Map();
    private _builderComputedColumns: Map<string, Map<string, EnvironmentBoundEvaluable>> = new Map();
    private _fieldNameCache: FieldNameCache = new Map();
    private _lastSchema: TypeSchema | undefined;
    private _durableStorageController: DurableStorageController;
    private _didAddRowsFromOfflineQueue: boolean = false;
    private _unlistenPublishedAppData: (() => void) | undefined;

    private readonly _builderComputationModel: ComputationModelData = {
        computationModel: undefined,
        tableKeeperStore: new TableKeeperStore(
            tn =>
                new ComputationModelDataRowStore(
                    false,
                    () => this._tables.get(documentIDForTableName(tn)),
                    () => this._handler?.getAppUserData(),
                    () => this.fetchTableRows(tn, false)
                ),
            false
        ),
        observable: new Watchable<ComputationModel | undefined>(undefined),
    };
    private readonly _playerComputationModel: ComputationModelData = {
        computationModel: undefined,
        tableKeeperStore: new TableKeeperStore(
            tn =>
                new ComputationModelDataRowStore(
                    this._isBuilder,
                    () => this._tables.get(documentIDForTableName(tn)),
                    () => this._handler?.getAppUserData(),
                    () => this.fetchTableRows(tn, true)
                ),
            false
        ),
        observable: new Watchable<ComputationModel | undefined>(undefined),
    };

    // This is a bit of technical debt. The DurableStorageController needs a defined TableMutationHandler,
    // but can't get it until an initial schema shows up. So we give it a TemporaryUndefinedContainer
    // instead; it'll wait until the handler is actually defined and go about its business.
    private _handlerContainer: TemporaryUndefinedContainer<DocumentsFirestoreHandlerImpl> =
        new TemporaryUndefinedContainer();

    constructor(
        private readonly _database: Database,
        private _schema: TypeSchema | undefined,
        authenticator: AppUserAuthenticator,
        private readonly _isBuilder: boolean,
        private readonly _needsOCM: boolean,
        private readonly _needsNCM: boolean,
        private readonly _deviceIDOverride: string | undefined,
        private readonly _callbacks: FirestoreDataStoreCallbacks = {}
    ) {
        logInfo("********* new data store", _schema);

        this._appFacilities = authenticator.appFacilities;
        this._appID = authenticator.appID;
        this._appUserID = authenticator.appUserID;
        this._appUserEmail = authenticator.virtualEmail;

        authenticator.addCallback((appUserID, realEmail, virtualEmail) => {
            if (appUserID === undefined) return;
            this.setAppUser(appUserID, virtualEmail ?? realEmail);
        });

        if (this._appUserID === undefined) {
            // This is another place where we set a ##previewAsUser.  I don't
            // think this is a good place to initiate this.
            dontAwait(authenticator.tryGetAppUserID());
        }

        this._appDataForTables = {};
        if (_needsOCM && _isBuilder) {
            this._builderDataForTables = {};
        }
        if (_schema !== undefined) {
            this.addTables(_schema.tables);
        }

        this._durableStorageController = this.makeDurableStorageController();
    }

    private makeDurableStorageController(): DurableStorageController {
        return storageControllerConstructor(
            {
                appID: () => this._appID,
                appUserID: () => this._appUserID,
                appFacilities: () => this._appFacilities,
                database: () => this._database,
                tableMutationHandler: () => this._handlerContainer.getValue(),
                schema: () => this.schema,
                userAgnosticColumnsForTable: (tableName: TableName) => this.getUserAgnosticColumnsForTable(tableName),
                userSpecificColumnsForTable: (tableName: TableName) => this.getUserSpecificColumnsForTable(tableName),
                makeOfflineQueue: (name: string, onOnline: () => void) => {
                    const queue: OfflineQueue<AddRowQueueItem> | undefined = this._appEnvironment?.makeOfflineQueue?.(
                        name,
                        onOnline,
                        i => i.data.jobID
                    );
                    if (queue === undefined) return undefined;
                    const schema = this._schema;
                    if (!this._didAddRowsFromOfflineQueue && schema !== undefined) {
                        this._didAddRowsFromOfflineQueue = true;
                        queue.forEach(item => {
                            const { tableName, columnValues } = item.data;
                            const rowData = convertJSONObjectToRowData(this._database, columnValues);
                            const datas = rowData.getObjectValue();
                            this._handler?.addRowToTable(tableName, {
                                values: columnValues as Record<string, PrimitiveValue>,
                                datas: datas as Record<string, Data>,
                            });
                        });
                    }
                    return queue;
                },
            },
            this._deviceIDOverride,
            this._isBuilder
        );
    }

    private getUserAgnosticColumnsForTable(tableName: TableName): string[] {
        const targetTable = this._tables.get(documentIDForTableName(tableName));
        if (targetTable === undefined) return [];

        return targetTable.columns.filter(isUserAgnosticDataColumn).map(c => c.name);
    }

    private getUserSpecificColumnsForTable(tableName: TableName): string[] {
        const targetTable = this._tables.get(documentIDForTableName(tableName));
        if (targetTable === undefined) return [];

        return targetTable.columns.filter(c => c.isUserSpecific === true).map(c => c.name);
    }

    public getComputationModelObservable(forBuilder: boolean): ChangeObservable<ComputationModel | undefined> {
        return forBuilder ? this._builderComputationModel.observable : this._playerComputationModel.observable;
    }

    public setAppEnvironment(appEnvironment: MinimalAppEnvironment): void {
        assert(this._appEnvironment === undefined);
        this._appEnvironment = appEnvironment;
        if (
            this._playerComputationModel.computationModel === "requested" ||
            this._builderComputationModel.computationModel === "requested"
        ) {
            this.updateComputationModel();
        }
    }

    public appEnvironmentUpdated(): void {
        if (
            typeof this._playerComputationModel.computationModel === "object" ||
            typeof this._builderComputationModel.computationModel === "object"
        ) {
            this.updateComputationModel();
        }
        this._durableStorageController.appEnvironmentUpdated();
    }

    private getLoadRowQuota(): number {
        const proAppRowQuota = eminenceFlags[Eminence.Pro].rowQuota;

        if (this._isBuilder) return proAppRowQuota;
        return this._appEnvironment?.getQuotaValues?.(QuotaKind.RowsUsed)?.limit ?? proAppRowQuota;
    }

    private setAppUser(appUserID: string, appUserEmail: string | undefined): void {
        if (this._appUserID === appUserID && this._appUserEmail === appUserEmail) return;

        if (this._appUserID === appUserID && !this._isBuilder) {
            // Why do we exit early here?  Because we don't care about the
            // email?
            return;
        }

        this._appUserID = appUserID;
        this._appUserEmail = appUserEmail;

        if (this._handler !== undefined) {
            this._handler.setAppUser(appUserID, appUserEmail);
        }
        this._durableStorageController.appUserChanged();
    }

    private addTableData(
        tableName: TableName,
        dataForTables: DataForTables
    ): Data | SettableForwardingValueProducer<AppOther, AppEnvironment> {
        const documentID = documentIDForTableName(tableName);
        let data = dataForTables[documentID];
        if (data === undefined) {
            data = new SettableForwardingValueProducer();
            dataForTables[documentID] = data;
        }
        return data;
    }

    private addTables(tables: Iterable<TableGlideType>): void {
        for (const table of tables) {
            const name = getTableName(table);
            this._tables.set(documentIDForTableName(name), table);
            this.addTableData(name, this._appDataForTables);
            if (this._builderDataForTables !== undefined) {
                this.addTableData(name, this._builderDataForTables);
            }
        }
    }

    private listenIfNecessary(): void {
        if (this._lastSchema === undefined) {
            const initialSchema = this._appFacilities.getInitialSchemaForAppID(this._appID);
            if (initialSchema !== undefined) {
                const setSchema = isolateTypeSchema(initialSchema);
                this.setSchema(setSchema);
                this._lastSchema = setSchema;
            }
        }

        if (this._listeningToPublishedAppData) return;
        this._listeningToPublishedAppData = true;

        this._unlistenPublishedAppData = listenToPublishedAppData(this._database, this._appID, appData => {
            logInfo("published app data", appData);

            if (compareSchemas(this._lastSchema, appData.schema, false)?.tablesOrColumns === true) {
                this.setSchema(appData.schema);
            }
            this._lastSchema = appData.schema;
        });
    }

    public get schema(): TypeSchema {
        return this._schema ?? makeTypeSchema([]);
    }

    public getComputedColumn(
        tableName: TableName,
        columnName: string,
        formula: Formula,
        isBuilder: boolean,
        forDataEditor: boolean
    ): EnvironmentBoundEvaluable | undefined {
        assert(!tableName.isSpecial);

        const computedColumns = forDataEditor ? this._builderComputedColumns : this._appComputedColumns;

        let tableEntry = computedColumns.get(tableName.name);
        if (tableEntry === undefined) {
            tableEntry = new Map();
            computedColumns.set(tableName.name, tableEntry);
        }

        let evaluable = tableEntry.get(columnName);
        if (evaluable === undefined) {
            const appEnvironment = defined(this._appEnvironment);
            // FIXME: Ugh, we're only passing a schema in here because of the
            // callbacks issue (see `GlideApp.tsx`).
            const unboundEvaluable = appEnvironment.getComputedColumn?.(
                this.schema,
                tableName,
                columnName,
                formula,
                isBuilder,
                forDataEditor
            );
            if (unboundEvaluable === undefined) return undefined;

            const env = appEnvironment.makeRootEvalEnvironment?.();
            if (env === undefined) return undefined;

            evaluable = new BindEnvironmentEvaluable(unboundEvaluable, env);
            tableEntry.set(columnName, evaluable);
        }

        return evaluable;
    }

    private updateComputationModelWithData(data: ComputationModelData, inspector: SchemaInspector): void {
        if (this._appEnvironment === undefined) {
            assert(typeof data.computationModel !== "object");
            return;
        }

        if (typeof data.computationModel === "object") {
            data.computationModel.retire();
        }

        data.computationModel = new ComputationModelImpl(inspector, data.tableKeeperStore, this._appEnvironment, false);
        data.observable.current = data.computationModel;
    }

    private updateComputationModel(): void {
        const inspector = makeSimpleSchemaInspector(this.schema, this._appEnvironment?.userProfileTableInfo);

        this.updateComputationModelWithData(this._playerComputationModel, inspector);
        this.updateComputationModelWithData(this._builderComputationModel, inspector);
    }

    public setSchema(schema: TypeSchema | undefined): void {
        this._fieldNameCache.clear();

        const oldTables = this.schema.tables;

        // If we're running in the builder, the schema could change
        // and we have to adapt.
        if (schema !== undefined) {
            this._schema = schema;
            this.addTables(schema.tables);
        }

        if (this._handler !== undefined) {
            // ##computationModelSchemaChange:
            // Here we update the computation model when the computed columns
            // have changed in the schema, which, in the old computation
            // model, is the only reason it needs to be updated.  The new
            // computation model probably has more reasons to do that, for
            // example when the user profile changes in the app description.
            // In addition to that, this is not the code path we want to take
            // when the app creator plays with computed columns in the data
            // editor, because it's probably too slow.
            const ownersChanged = schema !== undefined && didEmailOwnersColumnsChange(oldTables, schema.tables);
            const changedComputedColumns = definedMap(schema, s => getChangedComputedColumns(oldTables, s.tables));

            if (changedComputedColumns !== undefined) {
                // We completely reset computed columns.  Existing handlers will
                // rebuild them when we notify them about the app data update.
                this._builderComputedColumns.clear();
                this._appComputedColumns.clear();
            }

            this.updateComputationModel();
            this._handler.onPublishedAppDataUpdate(ownersChanged, changedComputedColumns);
            return;
        }

        this.updateComputationModel();
        this._handler = new DocumentsFirestoreHandlerImpl(
            this._appFacilities,
            this._database,
            this._tables,
            this._playerComputationModel.tableKeeperStore,
            this._builderComputationModel.tableKeeperStore,
            this._dataRowStores,
            this._fieldNameCache,
            this._appDataForTables,
            this._builderDataForTables,
            this._appID,
            this._appUserID,
            this._appUserEmail,
            this._isBuilder,
            this._needsOCM,
            this._needsNCM,
            (t, c, s, b, d) => this.getComputedColumn(t, c, s, b, d),
            () => this.getLoadRowQuota(),
            this._durableStorageController,
            this._callbacks
        );
        this._handlerContainer.setValue(this._handler);

        for (const documentID of this._tablesFetched) {
            // Check whether we ever learned about this table from a schema
            if (this._appDataForTables[documentID] === undefined) continue;

            const table = this._tables.get(documentID);
            if (table === undefined) {
                logError("Table not found", documentID);
                continue;
            }
            this._handler.fetchTableRows(getTableName(table));
        }

        for (const [link, [forwardingVP, multiple, forBuilder]] of this._linksWithoutHandlers.entries()) {
            let vp: ValueProducer;
            if (!multiple) {
                vp = this._handler.fetchSingleLinkRow(link, forBuilder);
            } else {
                vp = this._handler.fetchMultipleLinkRows(link, forBuilder);
            }
            forwardingVP.setForwardee(vp);
        }

        this._linksWithoutHandlers.clear();
    }

    public userProfileTableChanged(): void {
        this.updateComputationModel();
    }

    public modifyComputedColumns(
        removed: ReadonlyMap<string, readonly string[]>,
        modified: ReadonlyMap<string, readonly TableColumn[]>,
        moved: ReadonlyMap<string, readonly [string, number][]>,
        setProtected: ReadonlyMap<string, readonly [string, boolean][]>,
        rowIDColumns: ReadonlyMap<string, string | undefined>
    ): void {
        if (this._appEnvironment?.modifyColumnsInSchema === undefined) return;
        const { schema } = this._appEnvironment.modifyColumnsInSchema(
            this.schema,
            removed,
            modified,
            moved,
            setProtected,
            rowIDColumns
        );
        this.setSchema(schema);
    }

    public setEmailOwnersColumns(tableName: TableName, emailOwnersColumn: readonly string[]): void {
        if (this._appEnvironment === undefined) return;

        const result = setEmailOwnersColumns(this.schema, [[tableName, emailOwnersColumn]]);
        if (typeof result === "number") return;

        const [schema] = result;
        this.setSchema(schema);
    }

    public fetchTableRows(tableName: TableName, forBuilder: boolean): ValueProducer {
        const dataForTables = this._needsOCM
            ? forBuilder
                ? defined(this._builderDataForTables)
                : this._appDataForTables
            : this._appDataForTables;
        const data = this.addTableData(tableName, dataForTables);

        this.listenIfNecessary();

        this._tablesFetched.add(documentIDForTableName(tableName));
        if (this._handler !== undefined) {
            this._handler.fetchTableRows(tableName);
        }

        return data;
    }

    private getValueProduerForLinkWithoutHandler(
        link: ValueProducer,
        multiple: boolean,
        forBuilder: boolean
    ): ValueProducer {
        const entry = this._linksWithoutHandlers.get(link);
        if (entry !== undefined) {
            const [existingVP, isMultiple] = entry;
            assert(isMultiple === multiple);
            return existingVP;
        }
        const vp = new SettableForwardingValueProducer<AppOther, AppEnvironment>();
        this._linksWithoutHandlers.set(link, [vp, multiple, forBuilder]);
        return vp;
    }

    public fetchSingleLinkRow(link: ValueProducer, forBuilder: boolean): ValueProducer {
        this.listenIfNecessary();

        if (this._handler === undefined) {
            return this.getValueProduerForLinkWithoutHandler(link, false, forBuilder);
        }
        return this._handler.fetchSingleLinkRow(link, forBuilder);
    }

    public fetchMultipleLinkRows(link: ValueProducer, forBuilder: boolean): ValueProducer {
        this.listenIfNecessary();

        if (this._handler === undefined) {
            return this.getValueProduerForLinkWithoutHandler(link, true, forBuilder);
        }
        return this._handler.fetchMultipleLinkRows(link, forBuilder);
    }

    public async addRowToTable(
        tableName: TableName,
        columnValuesVP: ValueProducer,
        columnNames: ReadonlySet<string> | undefined,
        addToData: boolean,
        fromDataEditor: boolean
    ): Promise<{ didAdd: boolean; row: ValueProducer | undefined }> {
        // There's no Row ID yet, so we have to do this:
        // const rowIndex = this._durableStorageController.createTemporaryRowIndex();

        const valuesAndDatas = await extractColumnValuesFromValueProducer(columnValuesVP, columnNames);
        if (valuesAndDatas === undefined) return { didAdd: false, row: undefined };
        const { values, datas } = valuesAndDatas;

        // FIXME: Support special tables, too
        assert(!tableName.isSpecial);

        const table = this._tables.get(documentIDForTableName(tableName));

        let rowIndex: RowIndex | undefined;
        const rowIDColumn = table?.rowIDColumn;
        if (rowIDColumn !== undefined) {
            const rowID = makeRowID();
            values[rowIDColumn] = rowID;
            datas[rowIDColumn] = new Data<AppOther, AppEnvironment>(rowID);
            rowIndex = { keyColumnName: rowIDColumn, keyColumnValue: rowID };
        }

        let row: ValueProducer | undefined;
        // FIXME: Have the handler somehow interact with the durable storage
        // controller.  It's weird that this can succeed, but then the storage
        // controller can fail.
        if (this._handler !== undefined && addToData) {
            row = this._handler.addRowToTable(tableName, valuesAndDatas);
        }

        const didAdd = await this._durableStorageController.addRowToTable(tableName, rowIndex, values, fromDataEditor);

        return { didAdd, row };
    }

    public async setColumnsInRow(
        tableName: TableName,
        rowIndex: RowIndex,
        columnValuesVP: ValueProducer,
        setUnderlyingData: boolean,
        withDebounce: boolean,
        fromDataEditor: boolean,
        onCompletion?: () => void
    ): Promise<void> {
        // FIXME: Support special tables, too
        assert(!tableName.isSpecial);

        const table = this._tables.get(documentIDForTableName(tableName));
        if (table === undefined) return;

        const valuesAndDatas = await extractColumnValuesFromValueProducer(columnValuesVP, undefined);
        if (valuesAndDatas === undefined) return;

        if (setUnderlyingData && this._handler !== undefined) {
            this._handler.setColumnsInRow(tableName, rowIndex, valuesAndDatas);
        }

        if (this._appUserID === undefined) {
            // If no user is logged in then there's no point sending
            // user-specific columns to the backend, since it won't save it
            // anyway.
            for (const c of table.columns) {
                if (c.isUserSpecific !== true) continue;
                delete valuesAndDatas.values[c.name];
            }
        }

        if (Object.keys(valuesAndDatas.values).length === 0) return;

        // The durable storage controller will wait until the debounce is
        // done, which is too long to block the app for.  We've already
        // evaluated the values to set, and we've set them in the runtime
        // data.  This is just about writing the action out, which can happen
        // asynchronously.
        dontAwait(
            this._durableStorageController.setColumnsInRow(
                tableName,
                rowIndex,
                valuesAndDatas.values,
                withDebounce,
                fromDataEditor,
                onCompletion
            )
        );
    }

    public async deleteRowAtIndex(
        tableName: TableName,
        rowIndex: RowIndex,
        setUnderlyingData: boolean,
        fromDataEditor: boolean
    ): Promise<void> {
        // FIXME: Support special tables, too
        assert(!tableName.isSpecial);

        if (setUnderlyingData && this._handler !== undefined) {
            this._handler.deleteRowAtIndex(tableName, rowIndex);
        }

        let onConfirm: ((serial: number) => void) | undefined;
        if (isBaseRowIndex(rowIndex)) {
            onConfirm = serial => this._handler?.confirmDeletedRow(tableName, rowIndex, serial);
        }
        await this._durableStorageController.deleteRowAtIndex(tableName, rowIndex, fromDataEditor, onConfirm);
    }

    public async deleteRow(tableName: TableName, row: ValueProducer, fromDataEditor: boolean): Promise<void> {
        const table = this._tables.get(documentIDForTableName(tableName));
        if (table === undefined) return;

        const rowInspector = row.getObjectInspector(undefined);
        if (!isLoadedDefined(rowInspector)) return;

        const baseRowIndex = loadedDefinedMap(rowInspector.getProperty(rowIndexColumnName)?.current, checkBaseRowIndex);

        let rowIndex: RowIndex | undefined;
        if (table.rowIDColumn === undefined) {
            rowIndex = baseRowIndex;
        } else {
            const rowID = loadedDefinedMap(rowInspector.getProperty(table.rowIDColumn)?.current, checkString);
            if (rowID !== undefined) {
                rowIndex = {
                    keyColumnName: table.rowIDColumn,
                    keyColumnValue: rowID,
                    rowIndexHint: baseRowIndex,
                };
            }
        }
        if (rowIndex === undefined) return;

        await this.deleteRowAtIndex(tableName, rowIndex, true, fromDataEditor);
    }

    public async clearTable(_tableName: TableName): Promise<void> {
        return panic("FIXME: Implement");
    }

    public addComputedColumnsToData(tableName: TableName, data: Data): void {
        if (this._handler === undefined) return;
        this._handler.addComputedColumnsToData(tableName, getForwardee(data));
        return;
    }

    public isRowOwnedByUser(tableName: TableName, row: Row): boolean {
        const table = this._tables.get(documentIDForTableName(tableName));

        const appUserData = this._handler?.getAppUserData();
        return isNewRowOwnedByUser(row, appUserData, table);
    }

    public resetFromUpstream(): void {
        this._handler?.resetFromFirestore();
    }

    public retire(): void {
        this._unlistenPublishedAppData?.();
        this._listeningToPublishedAppData = false;
        if (this._handler !== undefined) {
            this._handler.retire();
        }
        this._durableStorageController.retire();
    }
}
